import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Topo from "../Topo/topo";
import { iVendaAPI } from "../../servicos/intencaovenda";
import CurrencyInput from "react-currency-input-field";
import { FormField } from "../ui/form-field";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  normalizeCurrencyValue,
  currencyStringToCents,
  centsToCurrencyString,
} from "../../utils/currency";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "sonner";

const toStringSafe = (valor) => {
  if (valor === null || valor === undefined) {
    return "";
  }
  return String(valor);
};
const digitsOnly = (valor = "") => toStringSafe(valor).replace(/\D/g, "");
const safeTrim = (valor = "") => toStringSafe(valor).trim();
const sanitizePlaca = (valor = "") =>
  toStringSafe(valor).replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 7);
const sanitizeRenavam = (valor = "") => digitsOnly(valor).slice(0, 11);
const sanitizeHodometro = (valor = "") => digitsOnly(valor).slice(0, 7);
const sanitizeCep = (valor = "") => digitsOnly(valor).slice(0, 8);
const formatCep = (valor = "") => {
  const digits = sanitizeCep(valor);
  if (digits.length <= 2) {
    return digits;
  }
  if (digits.length <= 5) {
    return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}-${digits.slice(5)}`;
};
const formatCnpj = (valor = "") => {
  const digits = digitsOnly(valor).slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};
const formatCpf = (valor = "") => {
  const digits = digitsOnly(valor).slice(0, 11);
  return digits
    .replace(/^(\d{3})(\d)/, "$1.$2")
    .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/(\d{3})(\d)/, "$1-$2");
};
const formatCpfCnpj = (valor = "") => {
  const digits = digitsOnly(valor).slice(0, 14);
  if (digits.length <= 11) {
    return formatCpf(digits);
  }
  return formatCnpj(digits);
};

const isValidCnpj = (valor = "") => {
  const cnpj = digitsOnly(valor);
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) {
    return false;
  }
  const calcDigit = (base) => {
    let soma = 0;
    let fator = 2;
    for (let i = base.length - 1; i >= 0; i -= 1) {
      soma += Number(base[i]) * fator;
      fator = fator === 9 ? 2 : fator + 1;
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };
  const base = cnpj.slice(0, 12);
  const digito1 = calcDigit(base);
  const digito2 = calcDigit(base + digito1);
  return cnpj === base + digito1 + digito2;
};

const isValidCpf = (valor = "") => {
  const cpf = digitsOnly(valor);
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
    return false;
  }
  const calcDigit = (base, fatorInicial) => {
    let soma = 0;
    let fator = fatorInicial;
    for (const char of base) {
      soma += Number(char) * fator;
      fator -= 1;
    }
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };
  const digito1 = calcDigit(cpf.slice(0, 9), 10);
  const digito2 = calcDigit(cpf.slice(0, 10), 11);
  return cpf === cpf.slice(0, 9) + digito1 + digito2;
};

const handleCurrencyKeyDown = (evento, valorAtual, onChange) => {
  const { key, target } = evento;
  if (!(target instanceof HTMLInputElement)) {
    return;
  }

  const ehNumero = /^[0-9]$/.test(key);
  const estaNoFinal =
    target.selectionStart === target.selectionEnd &&
    target.selectionStart === target.value.length;

  if (ehNumero && estaNoFinal) {
    evento.preventDefault();
    const centavosAtual = currencyStringToCents(valorAtual);
    const proximoValor = centsToCurrencyString(
      Math.min(Number.MAX_SAFE_INTEGER, centavosAtual * 10 + Number(key)),
    );
    onChange(proximoValor);
    requestAnimationFrame(() => {
      target.setSelectionRange(target.value.length, target.value.length);
    });
    return;
  }

  if ((key === "Backspace" || key === "Delete") && estaNoFinal) {
    evento.preventDefault();
    const centavosAtual = currencyStringToCents(valorAtual);
    const proximoValor = centsToCurrencyString(Math.floor(centavosAtual / 10));
    onChange(proximoValor);
    requestAnimationFrame(() => {
      target.setSelectionRange(target.value.length, target.value.length);
    });
  }
};

function Editar() {
  const breadcrumbs = ["Home", "Intenção de Venda", "Editar"];
  const { hash } = useParams();

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      ve103_dsc_placa: "",
      ve103_num_renavam: "",
      ve103_vlr_venda: "0.00",
      ve103_num_hodometro: "",
      ve103_num_documento_vendedor: "",
      ve103_dsc_email_vendedor: "",
      ve103_num_documento_comprador: "",
      ve103_dsc_nome_comprador: "",
      ve103_dsc_email_comprador: "",
      ve103_dsc_uf_comprador: "",
      ve103_num_cep_comprador: "",
      ve103_dsc_rua_comprador: "",
      ve103_num_rua_comprador: "",
      ve103_dsc_complemento_comprador: "",
      ve103_dsc_bairro_comprador: "",
    },
    mode: "onBlur",
  });

  const cepValor = useWatch({ control, name: "ve103_num_cep_comprador" });
  const ultimoCepRef = useRef("");
  const [ve103_key_public, set103_key_public] = useState("");
  const [mostraSalvar, setmostraSalvar] = useState(true);
  const [mostraEnviar, setmostraEnviar] = useState(false);
  const [mostraNovo, setmostraNovo] = useState(false);

  const navigate = useNavigate();

  const baseInputClass =
    "form-input tw-flex tw-h-11 tw-w-full tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-50 tw-px-4 tw-py-2 tw-text-sm tw-text-slate-900 tw-shadow-inner tw-transition-all placeholder:tw-text-slate-400 hover:tw-border-indigo-300 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-indigo-400 focus-visible:tw-border-indigo-500 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 dark:tw-border-slate-700 dark:tw-bg-slate-800 dark:tw-text-slate-100 dark:placeholder:tw-text-slate-500 dark:hover:tw-border-indigo-400/40 dark:focus-visible:tw-ring-indigo-300/60";
  const currencyInputClass = baseInputClass;

  const Voltar = () => {
    navigate("/filiado/ivenda");
  };

  const NovoCadastro = () => {
    reset();
    set103_key_public("");
    setmostraSalvar(true);
    setmostraEnviar(false);
    setmostraNovo(false);
  };

  useEffect(() => {
    if (!hash) {
      toast.error("OPS Desconheço esta intenção de venda.");
      return;
    }

    const carregarDados = async () => {
      try {
        const resposta = await iVendaAPI.get(`/editar/${hash}`);
        const dadosBrutos = resposta?.data?.ivenda ?? resposta?.data ?? {};
        const dados = {
          ve103_dsc_placa: dadosBrutos.ve103_dsc_placa || "",
          ve103_num_renavam: dadosBrutos.ve103_num_renavam || "",
          ve103_vlr_venda: dadosBrutos.ve103_vlr_venda,
          ve103_num_hodometro: dadosBrutos.ve103_num_hodometro || "",
          ve103_num_documento_vendedor: dadosBrutos.ve103_num_documento_vendedor || "",
          ve103_dsc_email_vendedor: dadosBrutos.ve103_dsc_email_vendedor || "",
          ve103_num_documento_comprador: dadosBrutos.ve103_num_documento_comprador || "",
          ve103_dsc_nome_comprador: dadosBrutos.ve103_dsc_nome_comprador || "",
          ve103_dsc_email_comprador: dadosBrutos.ve103_dsc_email_comprador || "",
          ve103_dsc_uf_comprador: dadosBrutos.ve103_dsc_uf_comprador || "",
          ve103_num_cep_comprador: dadosBrutos.ve103_num_cep_comprador || "",
          ve103_dsc_rua_comprador: dadosBrutos.ve103_dsc_rua_comprador || "",
          ve103_num_rua_comprador: dadosBrutos.ve103_num_rua_comprador || "",
          ve103_dsc_complemento_comprador: dadosBrutos.ve103_dsc_complemento_comprador || "",
          ve103_dsc_bairro_comprador: dadosBrutos.ve103_dsc_bairro_comprador || "",
          ve103_num_atpve: dadosBrutos.ve103_num_atpve,
          ve103_key_public: dadosBrutos.ve103_key_public || hash,
        };

        set103_key_public(dados.ve103_key_public || "");
        reset({
          ve103_dsc_placa: sanitizePlaca(dados.ve103_dsc_placa),
          ve103_num_renavam: sanitizeRenavam(dados.ve103_num_renavam),
          ve103_vlr_venda: normalizeCurrencyValue(dados.ve103_vlr_venda || "0.00"),
          ve103_num_hodometro: sanitizeHodometro(dados.ve103_num_hodometro),
          ve103_num_documento_vendedor: formatCnpj(dados.ve103_num_documento_vendedor),
          ve103_dsc_email_vendedor: dados.ve103_dsc_email_vendedor,
          ve103_num_documento_comprador: formatCpfCnpj(dados.ve103_num_documento_comprador),
          ve103_dsc_nome_comprador: dados.ve103_dsc_nome_comprador,
          ve103_dsc_email_comprador: dados.ve103_dsc_email_comprador,
          ve103_dsc_uf_comprador: dados.ve103_dsc_uf_comprador,
          ve103_num_cep_comprador: dados.ve103_num_cep_comprador
            ? formatCep(dados.ve103_num_cep_comprador)
            : "",
          ve103_dsc_rua_comprador: dados.ve103_dsc_rua_comprador,
          ve103_num_rua_comprador: dados.ve103_num_rua_comprador,
          ve103_dsc_complemento_comprador: dados.ve103_dsc_complemento_comprador,
          ve103_dsc_bairro_comprador: dados.ve103_dsc_bairro_comprador,
        });
        setmostraEnviar(!dados.ve103_num_atpve);
        setmostraNovo(Boolean(dados.ve103_num_atpve));
      } catch (erro) {
        if (process.env.NODE_ENV !== "production") {
          console.error("[IVenda::Editar] Falha ao carregar dados:", erro);
        }
        let mensagem = "Ops! Ocorreu um erro inesperado.";
        if (erro?.response?.data?.message) {
          mensagem = erro.response.data.message;
        } else if (erro.response?.status === 401) {
          mensagem = "Ops! Sessão Expirada! Faça um novo login.";
        }
        toast.error(mensagem);
      }
    };

    carregarDados();
  }, [hash, reset]);

  const onSubmit = async (values) => {
    if (!hash) {
      toast.error("OPS Desconheço esta intenção de venda.");
      return;
    }

    setmostraSalvar(false);
    const payload = {
      ve103_dsc_placa: sanitizePlaca(values.ve103_dsc_placa),
      ve103_num_renavam: sanitizeRenavam(values.ve103_num_renavam),
      ve103_num_hodometro: sanitizeHodometro(values.ve103_num_hodometro),
      ve103_vlr_venda: normalizeCurrencyValue(values.ve103_vlr_venda || "0.00"),
      ve103_num_documento_vendedor: digitsOnly(values.ve103_num_documento_vendedor),
      ve103_dsc_email_vendedor: safeTrim(values.ve103_dsc_email_vendedor),
      ve103_num_documento_comprador: digitsOnly(values.ve103_num_documento_comprador),
      ve103_dsc_nome_comprador: safeTrim(values.ve103_dsc_nome_comprador),
      ve103_dsc_email_comprador: safeTrim(values.ve103_dsc_email_comprador),
      ve103_dsc_uf_comprador: values.ve103_dsc_uf_comprador,
      ve103_dsc_rua_comprador: safeTrim(values.ve103_dsc_rua_comprador),
      ve103_num_rua_comprador: safeTrim(values.ve103_num_rua_comprador),
      ve103_dsc_complemento_comprador: safeTrim(values.ve103_dsc_complemento_comprador),
      ve103_dsc_bairro_comprador: safeTrim(values.ve103_dsc_bairro_comprador),
      ve103_num_cep_comprador: sanitizeCep(values.ve103_num_cep_comprador),
    };

    try {
      await iVendaAPI.put(`/editar/${hash}`, payload);
      toast.success("Intenção de venda alterada com sucesso.");
      setmostraSalvar(true);
    } catch (erro) {
      let mensagem = "Ops! Ocorreu um erro inesperado.";
      if (erro?.response?.data?.message) {
        mensagem = erro.response.data.message;
      } else if (erro.response?.status === 401) {
        mensagem = "Ops! Sessão Expirada! Faça um novo login.";
      }
      toast.error(mensagem);
      setmostraSalvar(true);
    }
  };

  const onInvalidSubmit = () => {
    toast.error("Por favor, corrija os campos destacados.");
  };

  useEffect(() => {
    const sanitized = sanitizeCep(cepValor || "");
    if (sanitized.length === 8 && sanitized !== ultimoCepRef.current) {
      ultimoCepRef.current = sanitized;
      const controller = new AbortController();
      const buscarCep = async () => {
        try {
          const resposta = await fetch(`https://viacep.com.br/ws/${sanitized}/json/`, {
            signal: controller.signal,
          });
          if (!resposta.ok) {
            throw new Error("Falha ao buscar CEP.");
          }
          const dados = await resposta.json();
          if (dados.erro) {
            toast.error("CEP não encontrado.");
            return;
          }
          setValue("ve103_num_cep_comprador", formatCep(sanitized));
          if (dados.logradouro) {
            setValue("ve103_dsc_rua_comprador", dados.logradouro);
          }
          if (dados.bairro) {
            setValue("ve103_dsc_bairro_comprador", dados.bairro);
          }
          if (dados.uf) {
            setValue("ve103_dsc_uf_comprador", dados.uf);
          }
        } catch (erro) {
          if (erro.name === "AbortError") {
            return;
          }
          toast.error("Não foi possível buscar o CEP.");
        }
      };
      buscarCep();
      return () => controller.abort();
    }

    if (sanitized.length < 8) {
      ultimoCepRef.current = "";
    }
  }, [cepValor, setValue]);

  const EnviarDetran = async (event) => {
    event.preventDefault();

    if (!ve103_key_public) {
      toast.error("Registre a intenção antes de enviar ao Detran.");
      return;
    }

    setmostraEnviar(false);
    try {
      const resposta = await iVendaAPI.get(`/processar/${ve103_key_public}`);
      toast.success(
        `ATPVe (${resposta.data?.ivenda?.ve103_num_atpve}) registrado com sucesso.`,
      );
      setmostraNovo(true);
    } catch (erro) {
      let mensagem = "Ops! Ocorreu um erro inesperado.";
      if (erro?.response?.data?.message) {
        if (
          erro.response?.status === 401 ||
          (erro.response?.status === 406 &&
            erro?.response?.data?.message === "Token Inválido")
        ) {
          mensagem = "Ops! Sessão Expirada! Faça um novo login.";
        } else {
          mensagem = erro.response.data.message;
        }
      } else if (erro.response?.status === 401) {
        mensagem = "Ops! Sessão Expirada! Faça um novo login.";
      }
      toast.error(mensagem);
      setmostraEnviar(true);
    }
  };

  return (
    <div class="row">
      <div class="container">
        <Topo titulo="Editar Intenção de Venda" breadcrumbs={breadcrumbs} btnincluir={false} admin={false} />
        <div class="row row-padding-conteudo">
          <div class="col-12">
            <form class="row gy-4 gx-4" onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
              <input type="hidden" value={ve103_key_public} readOnly />
              <div class="col-12">
                <div class="accordion accordion-flush tw-flex tw-flex-col tw-gap-4" id="pnlIntencaoVenda">
                  <div class="accordion-item tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-100 tw-transition-colors dark:tw-border-slate-700 dark:tw-bg-slate-900">
                    <h2 class="accordion-header">
                      <button
                        class="accordion-button collapsed tw-!bg-slate-200 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 tw-transition-colors dark:tw-!bg-slate-800 dark:tw-text-slate-100"
                        type="button"
                        data-bs-toggle="collapse"
                        aria-expanded="true"
                      >
                        <strong>Veículo</strong>
                      </button>
                    </h2>
                    <div
                      id="flush-collapseOne"
                      class="accordion-collapse collapse show"
                      data-bs-parent="#pnlIntencaoVenda"
                    >
                      <div class="accordion-body tw-bg-slate-100 tw-py-6 tw-transition-colors dark:tw-bg-slate-900">
                        <div class="row gy-4 gx-4">
                          <div class="col-md-6">
                            <FormField
                              id="ve103_dsc_placa"
                              label="Placa"
                              error={errors.ve103_dsc_placa?.message}
                            >
                              <Controller
                                name="ve103_dsc_placa"
                                control={control}
                                rules={{
                                  required: "Informe a placa.",
                                  validate: (valor) =>
                                    sanitizePlaca(valor).length === 7 ||
                                    "A placa deve conter 7 caracteres.",
                                }}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id="ve103_dsc_placa"
                                    maxLength={7}
                                    onChange={(event) => field.onChange(sanitizePlaca(event.target.value))}
                                  />
                                )}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-6">
                            <FormField
                              id="ve103_num_renavam"
                              label="Renavam"
                              error={errors.ve103_num_renavam?.message}
                            >
                              <Controller
                                name="ve103_num_renavam"
                                control={control}
                                rules={{
                                  required: "Informe o Renavam.",
                                  validate: (valor) =>
                                    sanitizeRenavam(valor).length === 11 || "O Renavam deve conter 11 dígitos.",
                                }}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id="ve103_num_renavam"
                                    inputMode="numeric"
                                    maxLength={11}
                                    onChange={(event) => field.onChange(sanitizeRenavam(event.target.value))}
                                  />
                                )}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-3">
                            <FormField
                              id="ve103_num_hodometro"
                              label="Hodômetro"
                              error={errors.ve103_num_hodometro?.message}
                            >
                              <Controller
                                name="ve103_num_hodometro"
                                control={control}
                                rules={{
                                  required: "Informe o hodômetro.",
                                }}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id="ve103_num_hodometro"
                                    inputMode="numeric"
                                    maxLength={7}
                                    onChange={(event) => field.onChange(sanitizeHodometro(event.target.value))}
                                  />
                                )}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-6">
                            <FormField
                              id="ve103_vlr_venda"
                              label="Valor"
                              error={errors.ve103_vlr_venda?.message}
                            >
                              <Controller
                                name="ve103_vlr_venda"
                                control={control}
                                rules={{
                                  validate: (valor) =>
                                    currencyStringToCents(normalizeCurrencyValue(valor || "0.00")) > 0 ||
                                    "Informe o valor de venda.",
                                }}
                                render={({ field }) => (
                                  <CurrencyInput
                                    {...field}
                                    id="ve103_vlr_venda"
                                    intlConfig={{ locale: "pt-BR", currency: "BRL" }}
                                    allowNegativeValue={false}
                                    disableAbbreviations={true}
                                    decimalScale={2}
                                    decimalsLimit={2}
                                    fixedDecimalLength={2}
                                    maxLength={14}
                                    valueIsNumericString
                                    value={field.value ?? "0.00"}
                                    className={currencyInputClass}
                                    onValueChange={(value) => {
                                      const normalized =
                                        value === undefined
                                          ? field.value ?? "0.00"
                                          : normalizeCurrencyValue(value);
                                      field.onChange(normalized);
                                    }}
                                    onKeyDown={(event) =>
                                      handleCurrencyKeyDown(event, field.value ?? "0.00", field.onChange)
                                    }
                                  />
                                )}
                              />
                            </FormField>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="accordion-item tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-100 tw-transition-colors dark:tw-border-slate-700 dark:tw-bg-slate-900">
                    <h2 class="accordion-header">
                      <button
                        class="accordion-button collapsed tw-!bg-slate-200 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 tw-transition-colors dark:tw-!bg-slate-800 dark:tw-text-slate-100"
                        type="button"
                        data-bs-toggle="collapse"
                        aria-expanded="true"
                      >
                        <strong>Vendedor</strong>
                      </button>
                    </h2>
                    <div
                      id="flush-collapseTwo"
                      class="accordion-collapse collapse show"
                      data-bs-parent="#pnlIntencaoVenda"
                    >
                      <div class="accordion-body tw-bg-slate-100 tw-py-6 tw-transition-colors dark:tw-bg-slate-900">
                        <div class="row gy-4 gx-4">
                          <div class="col-md-3">
                            <FormField
                              id="ve103_num_documento_vendedor"
                              label="CNPJ"
                              error={errors.ve103_num_documento_vendedor?.message}
                            >
                              <Controller
                                name="ve103_num_documento_vendedor"
                                control={control}
                                rules={{
                                  required: "Informe o CNPJ.",
                                  validate: (valor) =>
                                    isValidCnpj(valor) || "Informe um CNPJ válido.",
                                }}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id="ve103_num_documento_vendedor"
                                    inputMode="numeric"
                                    maxLength={18}
                                    onChange={(event) => field.onChange(formatCnpj(event.target.value))}
                                  />
                                )}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-9">
                            <FormField
                              id="ve103_dsc_email_vendedor"
                              label="E-mail"
                              error={errors.ve103_dsc_email_vendedor?.message}
                            >
                              <Input
                                type="email"
                                id="ve103_dsc_email_vendedor"
                                placeholder="name@example.com.br"
                                {...register("ve103_dsc_email_vendedor", {
                                  required: "Informe o e-mail do vendedor.",
                                  pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: "Informe um e-mail válido.",
                                  },
                                })}
                              />
                            </FormField>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="accordion-item tw-overflow-hidden tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-slate-100 tw-transition-colors dark:tw-border-slate-700 dark:tw-bg-slate-900">
                    <h2 class="accordion-header">
                      <button
                        class="accordion-button collapsed tw-!bg-slate-200 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 tw-transition-colors dark:tw-!bg-slate-800 dark:tw-text-slate-100"
                        type="button"
                        data-bs-toggle="collapse"
                        aria-expanded="true"
                      >
                        <strong>Comprador</strong>
                      </button>
                    </h2>
                    <div
                      id="flush-collapseThree"
                      class="accordion-collapse collapse show"
                      data-bs-parent="#pnlIntencaoVenda"
                    >
                      <div class="accordion-body tw-bg-slate-100 tw-py-6 tw-transition-colors dark:tw-bg-slate-900">
                        <div class="row gy-4 gx-4">
                          <div class="col-md-3">
                            <FormField
                              id="ve103_num_documento_comprador"
                              label="CPF/CNPJ"
                              error={errors.ve103_num_documento_comprador?.message}
                            >
                              <Controller
                                name="ve103_num_documento_comprador"
                                control={control}
                                rules={{
                                  required: "Informe o documento do comprador.",
                                  validate: (valor) => {
                                    const digits = digitsOnly(valor);
                                    if (digits.length === 11) {
                                      return isValidCpf(digits) || "CPF inválido.";
                                    }
                                    if (digits.length === 14) {
                                      return isValidCnpj(digits) || "CNPJ inválido.";
                                    }
                                    return "Informe CPF (11) ou CNPJ (14) válido.";
                                  },
                                }}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id="ve103_num_documento_comprador"
                                    inputMode="numeric"
                                    maxLength={18}
                                    onChange={(event) => field.onChange(formatCpfCnpj(event.target.value))}
                                  />
                                )}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-9">
                            <FormField
                              id="ve103_dsc_nome_comprador"
                              label="Nome"
                              error={errors.ve103_dsc_nome_comprador?.message}
                            >
                              <Input
                                id="ve103_dsc_nome_comprador"
                                maxLength={300}
                                {...register("ve103_dsc_nome_comprador", {
                                  required: "Informe o nome do comprador.",
                                })}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-8">
                            <FormField
                              id="ve103_dsc_email_comprador"
                              label="E-mail"
                              error={errors.ve103_dsc_email_comprador?.message}
                            >
                              <Input
                                type="email"
                                id="ve103_dsc_email_comprador"
                                placeholder="name@example.com.br"
                                {...register("ve103_dsc_email_comprador", {
                                  required: "Informe o e-mail do comprador.",
                                  pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: "Informe um e-mail válido.",
                                  },
                                })}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-4">
                            <FormField
                              id="ve103_num_cep_comprador"
                              label="CEP"
                              error={errors.ve103_num_cep_comprador?.message}
                            >
                              <Controller
                                name="ve103_num_cep_comprador"
                                control={control}
                                rules={{
                                  required: "Informe o CEP.",
                                  validate: (valor) =>
                                    sanitizeCep(valor).length === 8 || "Informe o CEP com 8 dígitos.",
                                }}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id="ve103_num_cep_comprador"
                                    inputMode="numeric"
                                    maxLength={10}
                                    onChange={(event) => field.onChange(formatCep(event.target.value))}
                                  />
                                )}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-4">
                            <FormField
                              id="ve103_dsc_rua_comprador"
                              label="Rua"
                              error={errors.ve103_dsc_rua_comprador?.message}
                            >
                              <Input
                                id="ve103_dsc_rua_comprador"
                                maxLength={300}
                                {...register("ve103_dsc_rua_comprador", {
                                  required: "Informe a rua do comprador.",
                                })}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-4">
                            <FormField
                              id="ve103_num_rua_comprador"
                              label="Número"
                              error={errors.ve103_num_rua_comprador?.message}
                            >
                              <Input
                                id="ve103_num_rua_comprador"
                                maxLength={30}
                                {...register("ve103_num_rua_comprador", {
                                  required: "Informe o número.",
                                })}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-4">
                            <FormField id="ve103_dsc_complemento_comprador" label="Complemento">
                              <Input
                                id="ve103_dsc_complemento_comprador"
                                maxLength={120}
                                {...register("ve103_dsc_complemento_comprador")}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-4">
                            <FormField
                              id="ve103_dsc_bairro_comprador"
                              label="Bairro"
                              error={errors.ve103_dsc_bairro_comprador?.message}
                            >
                              <Input
                                id="ve103_dsc_bairro_comprador"
                                maxLength={120}
                                {...register("ve103_dsc_bairro_comprador", {
                                  required: "Informe o bairro.",
                                })}
                              />
                            </FormField>
                          </div>
                          <div class="col-md-4">
                            <FormField
                              id="ve103_dsc_uf_comprador"
                              label="UF"
                              error={errors.ve103_dsc_uf_comprador?.message}
                            >
                              <select
                                id="ve103_dsc_uf_comprador"
                                class={baseInputClass}
                                {...register("ve103_dsc_uf_comprador", {
                                  required: "Selecione o estado.",
                                })}
                              >
                                <option value="">Selecione</option>
                                {[
                                  "AC",
                                  "AL",
                                  "AP",
                                  "AM",
                                  "BA",
                                  "CE",
                                  "DF",
                                  "ES",
                                  "GO",
                                  "MA",
                                  "MT",
                                  "MS",
                                  "MG",
                                  "PA",
                                  "PB",
                                  "PR",
                                  "PE",
                                  "PI",
                                  "RJ",
                                  "RN",
                                  "RS",
                                  "RO",
                                  "RR",
                                  "SC",
                                  "SP",
                                  "SE",
                                  "TO",
                                ].map((uf) => (
                                  <option key={uf} value={uf}>
                                    {uf}
                                  </option>
                                ))}
                              </select>
                            </FormField>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-12 tw-flex tw-flex-wrap tw-justify-end tw-gap-3 tw-pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={Voltar}
                  className="tw-min-w-[120px] tw-text-slate-700 dark:tw-text-white"
                >
                  Voltar
                </Button>
                {mostraSalvar && (
                  <Button type="submit" className="tw-min-w-[140px]" disabled={isSubmitting}>
                    {isSubmitting ? "Salvando..." : "Salvar"}
                  </Button>
                )}
                {mostraEnviar && (
                  <Button type="button" onClick={EnviarDetran} className="tw-min-w-[140px]">
                    DetranPR
                  </Button>
                )}
                {mostraNovo && (
                  <Button type="button" onClick={NovoCadastro} className="tw-min-w-[140px]">
                    Novo
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Editar;
